// ════════════════════════════════════════════════════════════════════
// LYRAEA — Extras.js   ·   the viewers
// ────────────────────────────────────────────────────────────────────
// An extra or an item is one of exactly two shapes, and `KIND_META.holds`
// is the field that says which:
//
//   holds: "body"  → text, stored inline in Firebase. Rendered here.
//   holds: "src"   → a FILENAME on the asset CDN, resolved at render.
//
// That is deliberately a two-case switch and not a plugin architecture.
// A new kind is a row in KIND_META plus, at most, a branch in `Viewer`
// below — which is the whole reason the schema never said "song" and
// the reason adding books later will not be a rewrite.
//
// ⚠ NOTHING IN THIS FILE READS A PRICE OR A BALANCE. Viewers view. The
//   shelf decides what may be opened; a viewer that also enforced that
//   would be a second place for the rule to live, and the second place
//   is always the one that drifts.
// ════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { resolveCover } from "./CDN";
import { KIND_META, holdsBody } from "./lyraeaData";
import Sleeve from "./Sleeve";

const C = {
  void: "var(--void)", slab: "var(--slab)", edge: "var(--edge)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", bone: "var(--bone)",
  ash: "var(--ash)", smoke: "var(--smoke)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

const label = (kind) => KIND_META[kind]?.label || "Extra";
const glyph = (kind) => KIND_META[kind]?.glyph || "◈";

// ── TEXT ─────────────────────────────────────────────────────────────
// Lyrics are the reason this component is fussier than a <pre>.
//
// ⚠ BLANK LINES ARE STRUCTURE IN A LYRIC, NOT WHITESPACE. A verse break
//   is the only thing separating a verse from a chorus, so collapsing
//   runs of newlines — which every naive `.split("\n").filter(Boolean)`
//   does — destroys the shape of the song while looking like tidying.
//   Runs are preserved and rendered as a gap.
export function TextBody({ body, monospace }) {
  const blocks = String(body || "").replace(/\r\n/g, "\n").split(/\n{2,}/);
  return (
    <div style={{
      fontFamily: monospace ? C.mono : C.sans,
      fontSize: 16, lineHeight: 1.75, color: C.bone,
      whiteSpace: "pre-wrap", wordBreak: "break-word",
    }}>
      {blocks.map((b, i) => (
        <div key={i} style={{ marginBottom: i === blocks.length - 1 ? 0 : 22 }}>{b}</div>
      ))}
    </div>
  );
}

// ── PICTURE ──────────────────────────────────────────────────────────
// Same lesson the covers taught, applied to a full-size view: a CSS
// background cannot report failure, so this is an <img> with an onError
// and a generated sleeve underneath it. A broken filename degrades to
// art instead of to a black rectangle nobody can diagnose.
function Picture({ src, title, seed }) {
  const url = resolveCover(src);
  const [broken, setBroken] = useState(false);

  if (!url || broken) {
    return (
      <div style={{ position: "relative", width: "100%", aspectRatio: "1" }}>
        <Sleeve seed={seed || title || src} title={title} size="100%" />
        {broken && (
          <div style={{
            position: "absolute", left: 0, right: 0, bottom: 0, padding: "7px 9px",
            fontFamily: C.mono, fontSize: 9, letterSpacing: ".14em",
            color: C.ash, background: "#000000aa",
          }}>
            {/* The one question worth asking first, said out loud, because
                lyraea.pages.dev serves index.html for every path and a bad
                asset URL comes back as HTML with a 200 rather than a 404. */}
            FILE NOT FOUND ON THE ASSET HOST — CHECK THE FILENAME
          </div>
        )}
      </div>
    );
  }

  return (
    <img
      src={url} alt={title || ""}
      onError={() => setBroken(true)}
      style={{
        display: "block", width: "100%", height: "auto",
        maxHeight: "70vh", objectFit: "contain",
        background: C.void, border: `1px solid ${C.edge}`,
      }}
    />
  );
}

// ── A FILE YOU KEEP ──────────────────────────────────────────────────
// A book is not something we render; it is something you take away. No
// in-app reader, deliberately — a half-good PDF viewer is worse than the
// one already built into the browser.
function FileCard({ src, title }) {
  const url = resolveCover(src);
  if (!url) {
    return <Empty>No file attached yet.</Empty>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{
      display: "flex", alignItems: "center", gap: 12, textDecoration: "none",
      border: `1px solid ${C.edge}`, background: C.slab, padding: "14px 16px",
      color: C.bone, fontFamily: C.sans, fontSize: 15,
    }}>
      <span style={{ fontFamily: C.display, fontSize: 22, color: C.gold }}>▤</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {title || "Open the file"}
      </span>
      <span style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".2em", color: C.smoke }}>OPEN ↗</span>
    </a>
  );
}

const Empty = ({ children }) => (
  <div style={{
    fontFamily: C.mono, fontSize: 10, letterSpacing: ".18em",
    color: C.smoke, textAlign: "center", padding: "34px 10px",
    border: `1px dashed ${C.edge}`,
  }}>{children}</div>
);

// ── THE SWITCH ───────────────────────────────────────────────────────
export function Viewer({ thing }) {
  if (!thing) return <Empty>NOTHING HERE</Empty>;
  const kind = thing.kind;

  if (holdsBody(kind)) {
    if (!String(thing.body || "").trim()) return <Empty>NOTHING WRITTEN YET</Empty>;
    // Lyrics get the mono face — it is the one text kind where the line
    // breaks are the art and a proportional face fights them.
    return <TextBody body={thing.body} monospace={kind === "lyrics"} />;
  }
  if (kind === "book") return <FileCard src={thing.src} title={thing.title} />;
  return <Picture src={thing.src} title={thing.title} seed={thing.id} />;
}

// ── ONE EXTRA, COLLAPSED UNTIL ASKED FOR ─────────────────────────────
// Lyrics open by default because that is what somebody tapping EXTRAS on
// a playing track came for. Everything else starts folded, so a release
// with six attachments is still a screen you can read.
export function ExtraCard({ extra, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen ?? extra.kind === "lyrics");

  return (
    <div style={{ borderBottom: `1px solid ${C.edge}` }}>
      <button
        className="ly-row"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          background: "none", border: "none", padding: "12px 4px",
          color: C.bone, textAlign: "left",
        }}
      >
        <span style={{ fontFamily: C.display, fontSize: 15, color: C.gold, width: 18 }}>
          {glyph(extra.kind)}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: "block", fontFamily: C.sans, fontSize: 15,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{extra.title || label(extra.kind)}</span>
          <span style={{
            display: "block", fontFamily: C.mono, fontSize: 9, letterSpacing: ".16em",
            color: C.smoke, textTransform: "uppercase", marginTop: 2,
          }}>
            {label(extra.kind)}{extra.albumId && !extra.trackId ? " · FROM THE ALBUM" : ""}
          </span>
        </span>
        <span style={{ fontFamily: C.mono, fontSize: 11, color: C.smoke }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div style={{ padding: "2px 4px 18px" }}>
          <Viewer thing={extra} />
        </div>
      )}
    </div>
  );
}

// ── THE PANEL ────────────────────────────────────────────────────────
// Used by the now-playing sheet and by the album page. Same list, same
// order, so a lyric looks the same wherever it is met.
export function ExtrasPanel({ extras = [], empty = "NOTHING ATTACHED TO THIS ONE" }) {
  if (!extras.length) return <Empty>{empty}</Empty>;
  return (
    <div>
      {extras.map((e) => <ExtraCard key={e.id} extra={e} />)}
    </div>
  );
}

export default ExtrasPanel;
